import { useState, useEffect } from "react";
import { Clock } from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Sign -> [startMonth, startDay, endMonth, endDay]
export const SIGN_RANGES = {
  Aries: [3, 21, 4, 19], Taurus: [4, 20, 5, 20], Gemini: [5, 21, 6, 20], Cancer: [6, 21, 7, 22],
  Leo: [7, 23, 8, 22], Virgo: [8, 23, 9, 22], Libra: [9, 23, 10, 22], Scorpio: [10, 23, 11, 21],
  Sagittarius: [11, 22, 12, 21], Capricorn: [12, 22, 1, 19], Aquarius: [1, 20, 2, 18], Pisces: [2, 19, 3, 20],
};

export function signRangeLabel(sign) {
  const [sm, sd, em, ed] = SIGN_RANGES[sign];
  return `${sd} ${MONTHS[sm - 1].slice(0, 3)} – ${ed} ${MONTHS[em - 1].slice(0, 3)}`;
}

const NOW = new Date();
const mLabel = (name) => (typeof window !== "undefined" && window.innerWidth < 360 ? name.slice(0, 3) : name);
const YEARS = Array.from({ length: NOW.getFullYear() - 1919 }, (_, i) => NOW.getFullYear() - i);

function daysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

const selectCls =
  "bg-[#0F1523] border border-[#D4AF37]/25 rounded-xl px-2.5 py-3 text-base min-w-0 w-full text-starlight focus:outline-none focus:border-[#D4AF37]/60 cursor-pointer";

export function BirthDatePicker({ value, onChange, testId, sign }) {
  const init = value ? value.split("-") : ["", "", ""];
  const [year, setYear] = useState(init[0] || "");
  const [month, setMonth] = useState(init[1] ? String(Number(init[1])) : "");
  const [day, setDay] = useState(init[2] ? String(Number(init[2])) : "");

  useEffect(() => {
    if (year && month && day) {
      onChange(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    } else {
      onChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, day]);

  const maxDay = daysInMonth(year, month);
  const days = Array.from({ length: maxDay }, (_, i) => i + 1);
  // clamp selected day if month/year change makes it invalid
  useEffect(() => {
    if (day && Number(day) > maxDay) setDay(String(maxDay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDay]);

  return (
    <div data-testid={testId} className="grid grid-cols-[1fr_1.5fr_1.15fr] gap-2">
      <select
        data-testid={testId ? `${testId}-day` : undefined}
        className={selectCls}
        value={day}
        onChange={(e) => setDay(e.target.value)}
      >
        <option value="">Day</option>
        {days.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
      <select
        data-testid={testId ? `${testId}-month` : undefined}
        className={selectCls}
        value={month}
        onChange={(e) => setMonth(e.target.value)}
      >
        <option value="">Month</option>
        {sign && SIGN_RANGES[sign] ? (
          <>
            <optgroup label={`${sign} months`}>
              {[SIGN_RANGES[sign][0], SIGN_RANGES[sign][2]].map((m) => (
                <option key={`s${m}`} value={m}>{mLabel(MONTHS[m - 1])}</option>
              ))}
            </optgroup>
            <optgroup label="All months">
              {MONTHS.map((name, i) =>
                [SIGN_RANGES[sign][0], SIGN_RANGES[sign][2]].includes(i + 1) ? null : (
                  <option key={i} value={i + 1}>{mLabel(name)}</option>
                ))}
            </optgroup>
          </>
        ) : (
          MONTHS.map((name, i) => <option key={i} value={i + 1}>{mLabel(name)}</option>)
        )}
      </select>
      <select
        data-testid={testId ? `${testId}-year` : undefined}
        className={selectCls}
        value={year}
        onChange={(e) => setYear(e.target.value)}
      >
        <option value="">Year</option>
        {YEARS.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}

export function BirthTimePicker({ value, onChange, testId }) {
  const [h, m] = value ? value.split(":") : ["", ""];
  const set = (hh, mm) => {
    if (hh === "" && mm === "") {
      onChange("");
    } else {
      onChange(`${(hh || "12").padStart(2, "0")}:${(mm || "00").padStart(2, "0")}`);
    }
  };
  return (
    <div data-testid={testId} className="flex items-center gap-2">
      <Clock size={18} className="gold-text shrink-0" />
      <select className={selectCls} value={h} onChange={(e) => set(e.target.value, m || "00")}>
        <option value="">Hour</option>
        {Array.from({ length: 24 }, (_, i) => (
          <option key={i} value={String(i).padStart(2, "0")}>
            {String(i).padStart(2, "0")}
          </option>
        ))}
      </select>
      <span className="text-[#A7B0C0]">:</span>
      <select className={selectCls} value={m} onChange={(e) => set(h || "12", e.target.value)}>
        <option value="">Min</option>
        {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")).map((mm) => (
          <option key={mm} value={mm}>
            {mm}
          </option>
        ))}
      </select>
    </div>
  );
}
